import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Cookie, CheckCircle2, AlertCircle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

export default function AdminSettings() {
  const [platforms, setPlatforms] = useState([]);
  const [platform, setPlatform] = useState("trumeet");
  const [s, setS] = useState(null);
  const [cookie, setCookie] = useState("");
  const [loading, setLoading] = useState(false);

  const loadPlatforms = useCallback(async () => {
    const { data } = await api.get("/admin/platforms");
    setPlatforms(data);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.get(`/admin/platform/${platform}/settings`);
    setS(data);
    loadPlatforms();
  }, [platform, loadPlatforms]);

  useEffect(() => { refresh(); }, [refresh]);

  const saveCookie = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/platform/${platform}/cookie`, { cookie });
      if (data.session_valid) {
        toast.success(`Imported ${data.cookies_imported} cookies — session is VALID`);
      } else {
        toast.warning(`Imported ${data.cookies_imported} cookies, but the session test FAILED. Make sure you were logged in when you exported.`);
      }
      setCookie("");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCookie(text);
    toast.success(`Loaded ${f.name} — click "Save cookie" to import`);
  };

  const testCookie = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/platform/${platform}/test`);
      if (data.ok) toast.success("Upstream cookie is valid");
      else toast.error("Upstream rejected the cookie. Refresh it.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleTest = async (val) => {
    try {
      await api.post(`/admin/platform/${platform}/test-mode?enabled=${val}`);
      toast.success(`Test mode ${val ? "enabled" : "disabled"}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const current = platforms.find((p) => p.key === platform);

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Upstream</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Platform Sessions</h1>
        <p className="text-zinc-500 mt-1">
          Configure the session cookies used to proxy recharges to each upstream recharge gateway.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="platform-switcher">
        {platforms.map((p) => (
          <button
            key={p.key}
            onClick={() => setPlatform(p.key)}
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              platform === p.key
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
            }`}
            data-testid={`platform-tab-${p.key}`}
          >
            {p.name}
            {p.has_cookie && p.last_status === "ok" && (
              <CheckCircle2 className="w-3.5 h-3.5 ml-1.5 inline text-green-500" />
            )}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              {current?.name || platform} — current state
            </div>
            <div className="mt-2 flex items-center gap-3">
              {s?.has_cookie ? (
                <span className="inline-flex items-center gap-1 text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4" /> Cookie present
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4" /> No cookie configured
                </span>
              )}
              {s?.cookie_preview && (
                <span className="font-mono text-xs text-zinc-500 truncate max-w-md">{s.cookie_preview}</span>
              )}
            </div>
            <div className="mt-2 text-xs text-zinc-500 font-mono">
              {s?.updated_at && `Updated ${new Date(s.updated_at).toLocaleString()}`}
              {s?.last_status && ` · last test: ${s.last_status}`}
            </div>
            <div className="mt-1 text-xs text-zinc-500 font-mono break-all">
              Upstream: {s?.api_base}{s?.recharge_path}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={testCookie} disabled={loading || !s?.has_cookie} data-testid="test-cookie-button">
              <RefreshCcw className="w-4 h-4 mr-1" /> Test
            </Button>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-zinc-100 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-medium text-zinc-900">Test mode</div>
            <div className="text-sm text-zinc-500">
              When ON, recharges are simulated as successful without calling the upstream. Useful during setup.
            </div>
          </div>
          <Switch
            checked={!!s?.test_mode}
            onCheckedChange={toggleTest}
            data-testid="test-mode-switch"
          />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md p-4 mb-4 leading-relaxed">
          <strong className="text-zinc-900">Easiest way (no tech needed):</strong>
          <ol className="list-decimal ml-4 mt-2 space-y-1">
            <li>Install the free Chrome/Edge extension <strong>“Get cookies.txt LOCALLY”</strong>.</li>
            <li>Open and log in to the <strong>{current?.name || platform}</strong> agent recharge page in that browser.</li>
            <li>Click the extension → <strong>Export</strong> (it downloads a <code>cookies.txt</code> file).</li>
            <li>Come back here → <strong>upload the file</strong> (or paste its contents) below → <strong>Save cookie</strong>.</li>
          </ol>
          <p className="mt-2">The system automatically keeps only the cookies it needs and tells you instantly if the session works.</p>
        </div>
        <form onSubmit={saveCookie} className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Cookie file or raw cookie string
              </Label>
              <label className="text-xs font-medium text-zinc-700 underline cursor-pointer" data-testid="cookie-upload-label">
                Upload cookies.txt
                <input type="file" accept=".txt,text/plain" className="hidden" onChange={onFile} data-testid="cookie-file-input" />
              </label>
            </div>
            <textarea
              value={cookie}
              onChange={(e) => setCookie(e.target.value)}
              placeholder="Paste the cookies.txt contents, or a raw Cookie header (hagouid=...; uaasCookie=...)"
              rows={6}
              className="w-full font-mono text-xs px-3 py-2 rounded-md border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950"
              data-testid="cookie-textarea"
            />
          </div>
          <Button
            type="submit"
            className="bg-zinc-900 hover:bg-zinc-800"
            disabled={loading || !cookie.trim()}
            data-testid="save-cookie-button"
          >
            <Cookie className="w-4 h-4 mr-1" /> Save cookie
          </Button>
        </form>
      </div>
    </div>
  );
}
